import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, Award } from 'lucide-react'
import { HtmlContent } from '@/components/ui/html-content'
import type { Course } from '@/types/course'

interface MasterclassCardProps {
  course: Course
}

/**
 * Card de masterclass da seção da home (/home e /landing).
 *
 * - status `coming_soon` → card apagado (opacity-60) + selo "Em breve",
 *   não clicável (teaser).
 * - status `published`   → opacidade cheia, sem selo, clicável pro detalhe
 *   do curso (/cursos/:slug).
 *
 * O instrutor vem de `course.mentor?.name` (renderizado como "com {nome}"
 * na fonte Caveat laranja). Sem thumbnail, mostra um ícone de fallback.
 */
const MasterclassCard = ({ course }: MasterclassCardProps) => {
  const [imageError, setImageError] = useState(false)
  const isComingSoon = course.status === 'coming_soon'
  const instructor = course.mentor?.name

  const inner = (
    <>
      <div className="relative aspect-video w-full overflow-hidden flex-shrink-0 bg-muted">
        {course.thumbnail && !imageError ? (
          <img
            src={course.thumbnail}
            alt={course.title}
            className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <Award className="w-10 h-10" />
          </div>
        )}
        {isComingSoon && (
          <div className="absolute top-3 right-3">
            <span className="bg-muted/90 backdrop-blur-sm text-muted-foreground text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Em breve
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col flex-grow p-4">
        <h4 className="text-lg font-semibold text-foreground mb-1 line-clamp-2">{course.title}</h4>
        {instructor ? (
          <span
            className="text-primary text-lg block mb-2"
            style={{ fontFamily: "'Caveat', cursive" }}
          >
            com {instructor}
          </span>
        ) : (
          <div className="h-7" />
        )}
        <div className="text-sm text-muted-foreground line-clamp-3">
          <HtmlContent content={course.description || ''} />
        </div>
      </div>
    </>
  )

  const baseClass =
    'relative overflow-hidden rounded-lg bg-card border border-border h-full flex flex-col'

  // `coming_soon`: teaser apagado, sem navegação.
  if (isComingSoon) {
    return <div className={`${baseClass} opacity-60`}>{inner}</div>
  }

  // `published`: card ativo, leva pro detalhe do curso.
  return (
    <Link
      to={`/cursos/${course.slug}`}
      className={`${baseClass} group transition-all hover:border-primary hover:shadow-lg`}
    >
      {inner}
    </Link>
  )
}

export default MasterclassCard
